<template>
  <v-menu
    v-model="open"
    @update:model-value="$emit('open', $event)"
    :close-on-content-click="true"
    :left="left"
    :right="right"
    data-cy="action-menu"
    data-testid="action-menu"
  >
    <template #activator="menuOptions">
      <button
        v-show="items.length"
        @click.prevent
        v-bind="menuOptions.props"
        :aria-label="$bct(label)"
        :class="fullButtonClass"
        :data-cy="buttonDataCy"
        :data-testid="buttonTitle || buttonTestId"
        :disabled="disabled"
      >
        <v-icon
          v-if="(buttonIcon && !buttonIconSide) || (buttonIconSide === 'left' && items.length > 0)"
          :color="buttonColor"
          :size="buttonIconSize"
          :start="buttonIconSide === 'left'"
          class="material-icons-outlined"
        >
          {{ buttonIcon }}
        </v-icon>

        <span v-if="buttonTitle">{{ noTranslate ? buttonTitle : $bct(buttonTitle) }}</span>

        <v-icon
          v-if="buttonIcon && buttonIconSide === 'right' && items.length > 0"
          :color="buttonColor"
          :size="buttonIconSize"
          end
        >
          {{ buttonIcon }}
        </v-icon>
      </button>
    </template>

    <v-list
      v-if="items.length > 0"
      class="bc-black b-1 py-0"
      data-testid="action-menu-list"
      tile
    >
      <div
        v-for="(item, index) in items"
        :key="item.title"
      >
        <v-list-item
          @click="handleItemClick(item)"
          :aria-label="noTranslate ? item.title : $bct(item.title)"
          :data-testid="item.testId || 'action-menu-item'"
          :disabled="item.disabled ? true : null"
          :href="item.href"
          :lines="item.subtitle ? 'two' : undefined"
          :subtitle="noTranslate ? item.subtitle : $bct(item.subtitle)"
          :title="noTranslate ? item.title : $bct(item.title)"
          :to="item.link"
          target="_blank"
        >
          <template
            v-if="!!item.avatar"
            #prepend
          >
            <v-avatar
              :size="avatarWidth(item)"
              rounded="0"
            >
              <v-icon
                :icon="item.avatar"
                :size="avatarWidth(item)"
              />
            </v-avatar>
          </template>

          <v-list-item-action v-if="!!item.action">
            <v-icon>{{ item.action }}</v-icon>
          </v-list-item-action>
        </v-list-item>

        <v-divider
          v-if="index + 1 < items.length"
          :key="index + '-divider'"
          class="bc-black"
        />
      </div>
    </v-list>
  </v-menu>
</template>

<script setup>
const $bct = inject('translator');

const emit = defineEmits([
  'action-clicked',
  'click:action:delete',
  'click:action:edit',
  'click:action:duplicate',
  'click:action:rename',
  'click:action:applicant',
  'click:action:award',
  'click:action:reviewer',
  'click:action:change',
  'click:action:transfer',
  'click:action:withdraw',
  'click:action:continue',
  'click:action:accept',
  'click:action:download',
  'click:action:expire',
  'click:action:void',
  'click:action:preview',
  'click:action:update_expiration',
  'click:action:update:stage',
  'click:action:destroy',
  'click:action:status',
  'click:action:draft-due-date',
  'click:action:switch',
  'click:action:create',
  'click:action:unsubmit',
  'click:action:release',
  'click:action:publish_to_providers',
  'click:action:publish_to_families',
  'click:action:reset',
  'click:action:revoke',
  'click:action:run',
  'click:action:go',
  'click:action:move',
  'click:action:footer',
  'click:action:header',
  'click:action:lock',
  'click:action:lock:preferences',
  'click:action:unlock:preferences',
  'click:action:schedule',
  'click:action:seats',
  'click:action:share',
  'click:action:save',
  'click:action:save-preview',
  'open',
]);

const props = defineProps({
  buttonBlock: {
    type: Boolean,
    default: false,
  },
  buttonClass: {
    type: String,
    default: '',
  },
  buttonColor: {
    default: 'black',
    type: String,
  },
  buttonDataCy: {
    type: String,
    default: 'action-menu-button',
  },
  buttonTitle: {
    type: String,
    default: '',
  },
  buttonIcon: {
    default: 'expand_more',
    type: String,
  },
  buttonIconSize: {
    default: 22,
    type: Number,
  },
  buttonIconSide: {
    type: String,
    default: null,
  },
  buttonOutlined: {
    type: Boolean,
    default: false,
  },
  buttonTestId: {
    type: String,
    default: 'action-menu-button',
  },
  buttonUnderlined: {
    type: Boolean,
    default: false,
  },
  dark: Boolean,
  disabled: {
    type: Boolean,
    default: false,
  },
  hoverUnderline: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: null,
  },
  label: {
    type: String,
    default: 'Modify element',
  },
  large: Boolean,
  left: Boolean,
  noTranslate: {
    type: Boolean,
    default: false,
  },
  right: Boolean,
  small: Boolean,
  useGenericEvents: {
    type: Boolean,
    default: false,
  },
  xLarge: Boolean,
  xSmall: Boolean,
});

const open = ref(false);

const fullButtonClass = computed(() => {
  let classes = [];

  if (props.buttonColor) {
    classes.push(`c-${props.buttonColor}`);
    classes.push(`bc-${props.buttonColor}`);
  }

  if (props.xLarge) {
    classes.push('fs-20');
  } else if (props.large) {
    classes.push('fs-18');
  } else if (props.small) {
    classes.push('fs-14');
  } else if (props.xSmall) {
    classes.push('fs-12');
  } else {
    classes.push('fs-16');
  }

  if (props.xLarge) {
    classes.push('px-8 py-4');
  } else if (props.large) {
    classes.push('px-5 py-3');
  } else if (props.small) {
    classes.push('px-2 py-2');
  } else {
    classes.push('px-4 py-2');
  }

  if (props.buttonOutlined) classes.push('b-1');
  if (props.buttonUnderlined) classes.push('bb-1');
  if (props.buttonBlock) classes.push('w-100pc mb-2');
  if (props.hoverUnderline) classes.push('hover-underline');
  if (props.disabled) classes.push('v-btn--disabled');

  if (props.buttonClass) {
    if (props.buttonClass.includes('px-')) {
      classes = classes.filter((c) => !c.startsWith('px-'));
    }
    classes.push(props.buttonClass);
  }

  classes.push('action-menu-button');
  classes.push('oo--1');

  return classes.join(' ');
});

function avatarWidth(item) {
  return item?.subtitle ? 25 : 20;
}

function handleItemClick(item) {
  if (props.useGenericEvents) {
    emit('action-clicked', { action: item.event, item });
  } else {
    emit('click:action:' + item.event, item);
  }
}
</script>

<style scoped>
.v-btn:not(.v-btn--round).x-x-large {
  height: 70px !important;
  min-width: 120px !important;
  font-size: 20px;
}

.action-menu-button.hover-underline span:hover {
  text-decoration: underline !important;
}

:deep(.v-list-item-subtitle) {
  line-height: 1.5rem;
}

.oo--1 {
  outline-offset: -1px;
}
</style>
